import { InfiniteData, QueryClient } from "@tanstack/react-query";
import { PlanningsDeliveryWithLegRunEntity } from "src/entities/PlanningsDelivery.entity";
import { PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY } from "src/hooks/useQueryPlanningsDeliveries";

export class PlanningsDeliveriesWithLegRunsCache {
  constructor(private queryClient: QueryClient) {}

  updateDeliveries(deliveries: PlanningsDeliveryWithLegRunEntity[]): void {
    const deliveriesCache = this.queryClient.getQueriesData({ queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY] });
    deliveriesCache.forEach(([queryCacheKey, cacheData]) => {
      if (cacheData && typeof cacheData === 'object' && 'pages' in cacheData) {
        const paginatedData = cacheData as InfiniteData<{
          ids: number[],
          data: PlanningsDeliveryWithLegRunEntity[],
          total_pages_count: number
        }>;
        if (paginatedData.pages.length > 0) {
          const updatedPages = paginatedData.pages.map((page) => {
            const filtered = deliveries.filter((it) => page.ids.includes(it.id));
            if (filtered.length === 0) return page;

            const newData = page.data.map((it) => {
              const newDelivery = filtered.find((deli) => deli.id === it.id);
              return newDelivery || it;
            });
            return { ...page, data: newData };
          });
          this.queryClient.setQueryData(queryCacheKey, { ...paginatedData, pages: updatedPages });
        }
      }
    });
  }

  addDeliveriesByDateRange(deliveries: PlanningsDeliveryWithLegRunEntity[]): void {
    const deliveryCache = this.queryClient.getQueriesData({ queryKey: [PLANNINGS_DELIVERIES_WITH_LEG_RUNS_QUERY_KEY] });
    deliveryCache.forEach(([queryCacheKey, cacheData]) => {
      const queryStartDate = new Date(queryCacheKey[1] as string);
      const queryEndDate = new Date(queryCacheKey[2] as string);
      queryStartDate.setHours(0, 0, 0, 0);
      queryEndDate.setHours(23, 59, 59, 999);

      // startAtが期間内にある配送データをフィルタリング
      const filteredDeliveries = deliveries.filter((delivery) => {
        if (!delivery.startAt) return false;
        const deliveryStartAt = new Date(delivery.startAt);
        return deliveryStartAt >= queryStartDate && deliveryStartAt <= queryEndDate;
      });

      // 既存のキャッシュデータに新しい配送データを追加
      if (cacheData && typeof cacheData === 'object' && 'pages' in cacheData && filteredDeliveries.length > 0) {
        const paginatedData = cacheData as InfiniteData<{
          ids: number[];
          total_pages_count: number;
          data: PlanningsDeliveryWithLegRunEntity[];
        }>;
        const updatedPages = [...paginatedData.pages];

        // 最後のページに新しい配送データを追加
        if (updatedPages.length > 0) {
          const lastPageIndex = updatedPages.length - 1;
          const lastPage = updatedPages[lastPageIndex];
          const filteredIds = filteredDeliveries.map((delivery) => delivery.id);

          updatedPages[lastPageIndex] = {
            ...lastPage,
            ids: [...lastPage.ids, ...filteredIds],
            data: [...lastPage.data, ...filteredDeliveries]
          };
        } else {
          // ページが存在しない場合は新しいページを作成
          const filteredIds = filteredDeliveries.map((delivery) => delivery.id);
          updatedPages.push({
            ids: filteredIds,
            data: filteredDeliveries,
            total_pages_count: 1
          });
        }

        this.queryClient.setQueryData(queryCacheKey, {
          ...paginatedData,
          pages: updatedPages
        });
      }
    });
  }
}
